// Gestão de Clientes RFID
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, Edit, Trash2, CreditCard, Search, 
  RefreshCw, CheckCircle, XCircle, History, RotateCcw,
  Star, Gift, ShoppingBag, TrendingUp, AlertTriangle, Wallet,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { ModuleInfo } from '../ui/module-info';
import { DataOriginBadge } from '../ui/data-origin';
import ConsultaOperacional from './ConsultaOperacional';

const GestaoClientes = ({ user }) => {
  const canManage = ['admin', 'responsavel'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSubstituirModal, setShowSubstituirModal] = useState(null);
  const [showHistoricoModal, setShowHistoricoModal] = useState(null);
  const [showResetModal, setShowResetModal] = useState(null);
  const [showEditarPontosModal, setShowEditarPontosModal] = useState(null);
  const [pontosAjuste, setPontosAjuste] = useState(0);
  const [motivoAjuste, setMotivoAjuste] = useState('');
  const [showCashbackModal, setShowCashbackModal] = useState(null);
  const [cashbackAjuste, setCashbackAjuste] = useState(0);
  const [cashbackAbsoluto, setCashbackAbsoluto] = useState(0);
  const [setAbsolutoMode, setSetAbsolutoMode] = useState(false);
  const [motivoCashback, setMotivoCashback] = useState('');
  const [historico, setHistorico] = useState(null);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  // Menu "Avançado" (Reset/Ajustes) — colapsado para reduzir risco operacional
  const [avancadoOpenFor, setAvancadoOpenFor] = useState(null);
  // 🆕 2026-05-22 — direção do dropdown (up/down) calculada dinamicamente para
  // clientes do fim da lista. Evita menu cortado fora do viewport.
  const [avancadoOpenDir, setAvancadoOpenDir] = useState('down');
  const avancadoRefs = useRef({});
  const [pesquisa, setPesquisa] = useState('');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);
  // Sorting & pagination for Movimentos
  const [movSort, setMovSort] = useState({ field: 'data', dir: 'desc' });
  const [movPage, setMovPage] = useState(0);
  const MOV_PER_PAGE = 25;
  const [novoIdCartao, setNovoIdCartao] = useState('');
  const [parceriasDisponiveis, setParceriasDisponiveis] = useState([]);
  const [locais, setLocais] = useState([]);
  const [locaisRegisto, setLocaisRegisto] = useState([]);
  const [localSugerido, setLocalSugerido] = useState('');
  
  const [formData, setFormData] = useState({
    id_cartao_rfid: '',
    nome_cliente: '',
    telefone: '',
    email: '',
    nif: '',
    morada: '',
    associacoes: [],
    data_nascimento: '',
    // Consentimentos RGPD (Opção B — substituem checkboxes legacy de notificação)
    consentimentos: {
      privacidade: false,
      promocional: false,
      personalizacao: false,
    },
    // 🆕 P2-B — Consentimentos promocionais por canal (estado independente).
    // null = não definido | true/false = aceite/recusado explícito.
    consentimentos_canais: { sms: null, whatsapp: null, email: null, push: null },
    // Estado original do servidor (para detectar "explícito" vs "legado" vs "não definido"
    // ao mostrar o badge visual ao colaborador).
    canais_estado_original: null,
    local_registo: ''
  });

  // Cache local: evita recarregar a mesma lista nos primeiros 30s
  const ultimoLoadRef = React.useRef({ ts: 0, params: null });

  useEffect(() => {
    loadClientes();
    loadParcerias();
    loadLocais();
  }, []);

  const loadLocais = async () => {
    try {
      const response = await api.get('/parcerias/locais');
      setLocais(response.data.locais || []);
      setLocaisRegisto(response.data.locais_registo || []);
      if (response.data.local_sugerido) setLocalSugerido(response.data.local_sugerido);
    } catch (error) {
      console.error('Erro ao carregar locais:', error);
    }
  };

  const loadClientes = async (incluirArquivados = false, _retry = 0, _force = false) => {
    // Cache: se carregámos há menos de 10s com os mesmos params, não refaz
    const params = incluirArquivados ? '?incluir_arquivados=true' : '';
    const agora = Date.now();
    if (
      !_force &&
      ultimoLoadRef.current.params === params &&
      agora - ultimoLoadRef.current.ts < 10000 &&
      clientes.length > 0
    ) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(`/parcerias/clientes-rfid${params}`);
      const data = response.data;
      const lista = Array.isArray(data) ? data : (data?.clientes || []);
      setClientes(lista);
      setLoading(false);
      ultimoLoadRef.current = { ts: agora, params };
      // Retry silencioso se vier vazio na 1ª tentativa (race condition pós-mount)
      if (lista.length === 0 && _retry < 1) {
        setTimeout(() => loadClientes(incluirArquivados, _retry + 1, true), 800);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      if (_retry < 2) {
        setTimeout(() => loadClientes(incluirArquivados, _retry + 1, true), 1000 * (_retry + 1));
        return;
      }
      setLoading(false);
      toast.error('Erro ao carregar clientes');
    }
  };

  const loadParcerias = async () => {
    try {
      const response = await api.get('/parcerias/entidades?estado=true');
      setParceriasDisponiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
    }
  };

  // 🆕 P2-B — Calcula estado por canal a partir do documento cliente (mesma lógica do backend `estado_consentimento_canais`).
  const computeCanaisEstado = (cliente) => {
    const canais = ['sms', 'whatsapp', 'email', 'push'];
    const legacy_keys = {
      sms: 'aceita_sms_promocional',
      whatsapp: 'aceita_whatsapp',
      email: 'aceita_email_promocional',
      push: 'aceita_push_promocional',
    };
    const promo = cliente?.consentimentos?.promocional || {};
    const umbrella = typeof promo.aceite === 'boolean' ? promo.aceite : null;
    const canais_bloco = promo.canais || {};
    const result = {};
    for (const c of canais) {
      const bloco = canais_bloco[c] || null;
      const v = bloco && typeof bloco.aceite === 'boolean' ? bloco.aceite : null;
      const legacy = typeof cliente?.[legacy_keys[c]] === 'boolean' ? cliente[legacy_keys[c]] : null;
      let status;
      let autoriza;
      if (v === true) { status = 'aceite_canal'; autoriza = true; }
      else if (v === false) { status = 'recusado_canal'; autoriza = false; }
      else if (umbrella === false) { status = 'recusado_umbrella'; autoriza = false; }
      else if (umbrella === true && legacy === true) { status = 'aceite_legado'; autoriza = true; }
      else { status = 'nao_definido'; autoriza = false; }
      result[c] = { status, autoriza, meta: bloco };
    }
    return result;
  };

  const handleOpenModal = (cliente = null) => {
    if (cliente) {
      setEditando(cliente);
      setFormData({
        id_cartao_rfid: cliente.id_cartao_rfid || '',
        nome_cliente: cliente.nome_cliente || cliente.nome || '',
        telefone: cliente.telefone || '',
        email: cliente.email || '',
        nif: cliente.nif || '',
        morada: cliente.morada || '',
        associacoes: cliente.associacoes || [],
        data_nascimento: cliente.data_nascimento || '',
        consentimentos: {
          privacidade: !!cliente?.consentimentos?.privacidade?.aceite,
          promocional: !!cliente?.consentimentos?.promocional?.aceite,
          personalizacao: !!cliente?.consentimentos?.personalizacao?.aceite,
        },
        // 🆕 P2-B — pré-popula canais com o valor explícito guardado (null se não-definido/legado).
        consentimentos_canais: (() => {
          const cb = cliente?.consentimentos?.promocional?.canais || {};
          const out = {};
          for (const c of ['sms', 'whatsapp', 'email', 'push']) {
            out[c] = (cb[c] && typeof cb[c].aceite === 'boolean') ? cb[c].aceite : null;
          }
          return out;
        })(),
        canais_estado_original: computeCanaisEstado(cliente),
      });
    } else {
      setEditando(null);
      // Ao criar novo cliente, todas as parcerias tipo "empresa" ficam ativas automaticamente
      // Perfis de benefício têm de ser selecionados manualmente
      const parceriasEmpresa = parceriasDisponiveis
        .filter(p => p.tipo_parceria === 'empresa')
        .map(p => p.id);
      
      setFormData({
        id_cartao_rfid: '',
        nome_cliente: '',
        telefone: '',
        email: '',
        nif: '',
        morada: '',
        associacoes: parceriasEmpresa,
        data_nascimento: '',
        consentimentos: {
          privacidade: false,
          promocional: false,
          personalizacao: false,
        },
        consentimentos_canais: { sms: null, whatsapp: null, email: null, push: null },
        canais_estado_original: null,
        local_registo: localSugerido || ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!editando && !formData.id_cartao_rfid.trim()) {
      toast.error('Introduza o ID do cartão ou do cliente');
      return;
    }

    if (!formData.nif || !formData.nif.trim()) {
      toast.error('O NIF é obrigatório');
      return;
    }

    try {
      // Garantir que todas as parcerias empresa estão sempre incluídas nas associações
      const parceriasEmpresaIds = parceriasDisponiveis
        .filter(p => p.tipo_parceria !== 'perfil_beneficio')
        .map(p => p.id);
      const associacoesFinais = [...new Set([...parceriasEmpresaIds, ...formData.associacoes])];

      if (editando) {
        const payload = {
          nome_cliente: formData.nome_cliente,
          telefone: formData.telefone,
          email: formData.email,
          nif: formData.nif,
          morada: formData.morada,
          associacoes: associacoesFinais,
          data_nascimento: formData.data_nascimento || null,
          consentimentos: {
            privacidade: !!formData.consentimentos?.privacidade,
            promocional: !!formData.consentimentos?.promocional,
            personalizacao: !!formData.consentimentos?.personalizacao,
          },
        };
        // 🆕 P2-B — Inclui apenas canais alterados pelo colaborador (≠ null).
        const canais_payload = {};
        for (const c of ['sms', 'whatsapp', 'email', 'push']) {
          const v = formData.consentimentos_canais?.[c];
          if (typeof v === 'boolean') canais_payload[c] = v;
        }
        if (Object.keys(canais_payload).length > 0) {
          payload.consentimentos_canais = canais_payload;
        }
        // Se está a associar um cartão RFID a um cliente sem cartão
        if (!editando.id_cartao_rfid && formData.id_cartao_rfid.trim()) {
          payload.id_cartao_rfid = formData.id_cartao_rfid.trim();
        }
        // Use _id for clients without RFID card (mobile-registered)
        if (editando.id_cartao_rfid) {
          await api.put(`/parcerias/clientes-rfid/${editando.id_cartao_rfid}`, payload);
        } else {
          await api.put(`/parcerias/clientes-rfid/by-id/${editando.id}`, payload);
        }
        toast.success('Cliente atualizado com sucesso');
      } else {
        await api.post('/parcerias/clientes-rfid', {
          ...formData,
          local_registo: formData.local_registo || null
        });
        toast.success('Cliente registado com sucesso');
      }

      setShowModal(false);
      loadClientes();
    } catch (error) {
      console.error('Erro ao guardar cliente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar cliente');
    }
  };

  // Helper: builds correct API path based on whether client has RFID card
  const clientApiPath = (cliente) => {
    if (cliente.id_cartao_rfid) return `/parcerias/clientes-rfid/${cliente.id_cartao_rfid}`;
    return `/parcerias/clientes-rfid/by-id/${cliente.id}`;
  };

  const handleToggleEstado = async (cliente) => {
    try {
      await api.put(clientApiPath(cliente), {
        estado_cartao: cliente.estado_cartao === 'Ativo' ? 'Inativo' : 'Ativo'
      });
      toast.success(`Cartão ${cliente.estado_cartao === 'Ativo' ? 'desativado' : 'ativado'}`);
      loadClientes();
    } catch (error) {
      toast.error('Erro ao alterar estado');
    }
  };

  const handleDesfidelizar = async (cliente) => {
    if (!window.confirm(`Tem a certeza que deseja desfidelizar ${cliente.nome_cliente}?\n\nO cliente será arquivado, os vales ativos cancelados e os pontos/cashback removidos.`)) return;
    try {
      await api.post(`/parcerias/clientes-rfid/${cliente.id_cartao_rfid}/desfidelizar`);
      toast.success(`${cliente.nome_cliente} desfidelizado e arquivado`);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desfidelizar');
    }
  };

  const handleSubstituir = async () => {
    if (!novoIdCartao.trim()) {
      toast.error('Introduza o novo ID do cartão');
      return;
    }

    try {
      await api.post(`/parcerias/clientes-rfid/${showSubstituirModal.id_cartao_rfid}/substituir`, null, {
        params: { novo_id_cartao: novoIdCartao }
      });
      toast.success('Cartão substituído com sucesso');
      setShowSubstituirModal(null);
      setNovoIdCartao('');
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao substituir cartão');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      if (showDeleteConfirm.id_cartao_rfid) {
        await api.delete(`/parcerias/clientes-rfid/${showDeleteConfirm.id_cartao_rfid}`);
      } else {
        await api.delete(`/parcerias/clientes-rfid/by-id/${showDeleteConfirm.id}`);
      }
      toast.success('Cliente arquivado. Pode reactivar a qualquer momento via "Mostrar arquivados".');
      setShowDeleteConfirm(null);
      loadClientes(mostrarArquivados);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao arquivar cliente');
    }
  };

  const handleReactivar = async (cliente) => {
    try {
      const id = cliente.id || cliente.id_cartao_rfid;
      const res = await api.post(`/parcerias/clientes-rfid/${id}/reactivar`);
      toast.success(`${res.data.nome} reactivado. Saldo: ${res.data.saldo_cashback || 0}€ / ${res.data.saldo_pontos || 0} pts`);
      loadClientes(mostrarArquivados);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reactivar');
    }
  };

  // Carregar histórico completo do cliente
  const loadHistorico = async (cliente) => {
    setHistoricoLoading(true);
    setShowHistoricoModal(cliente);
    setMovSort({ field: 'data', dir: 'desc' });
    setMovPage(0);
    try {
      // Build client identifier
      const clienteId = cliente.id_cartao_rfid || cliente.id;
      const response = await api.get(`/parcerias/clientes-rfid/${clienteId}/historico`);
      setHistorico(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico do cliente');
      // Set empty historico so UI still shows
      setHistorico({
        transacoes_pontos: [],
        vales: [],
        compras: [],
        missoes: []
      });
    } finally {
      setHistoricoLoading(false);
    }
  };

  // Reset de pontos do cliente (apenas admin)
  const handleResetPontos = async () => {
    if (!showResetModal) return;
    
    try {
      const clienteId = showResetModal.id_cartao_rfid || showResetModal.id;
      await api.post(`/parcerias/clientes-rfid/${clienteId}/reset-pontos`);
      toast.success(`Pontos de ${showResetModal.nome_cliente || 'Cliente'} foram zerados`);
      setShowResetModal(null);
      loadClientes();
    } catch (error) {
      console.error('Erro ao resetar pontos:', error);
      toast.error(error.response?.data?.detail || 'Erro ao zerar pontos');
    }
  };

  // Ajustar pontos do cliente
  const handleAjustarPontos = async () => {
    if (!showEditarPontosModal || pontosAjuste === 0) return;
    
    try {
      const clienteId = showEditarPontosModal.id_cartao_rfid || showEditarPontosModal.id;
      await api.post(`/parcerias/clientes-rfid/${clienteId}/ajustar-pontos`, {
        pontos: pontosAjuste,
        motivo: motivoAjuste || 'Ajuste manual'
      });
      toast.success(`Pontos ajustados com sucesso: ${pontosAjuste > 0 ? '+' : ''}${pontosAjuste}`);
      setShowEditarPontosModal(null);
      setPontosAjuste(0);
      setMotivoAjuste('');
      loadClientes();
    } catch (error) {
      console.error('Erro ao ajustar pontos:', error);
      toast.error(error.response?.data?.detail || 'Erro ao ajustar pontos');
    }
  };

  const handleAjustarCashback = async () => {
    if (!showCashbackModal) return;
    const isAbsoluto = setAbsolutoMode;
    const valor = isAbsoluto ? cashbackAbsoluto : cashbackAjuste;
    if (!isAbsoluto && cashbackAjuste === 0) return;
    if (isAbsoluto && cashbackAbsoluto === (showCashbackModal.saldo_cashback || 0)) return;
    try {
      const clienteId = showCashbackModal.id_cartao_rfid || showCashbackModal.id;
      const res = await api.post(`/parcerias/clientes-rfid/${clienteId}/ajustar-cashback`, {
        valor,
        motivo: motivoCashback || (isAbsoluto ? `Definir saldo a ${valor}€` : 'Ajuste admin'),
        set_absoluto: isAbsoluto,
      });
      toast.success(`Saldo cashback: ${res.data.saldo_atual?.toFixed(2)}€ (ajuste de ${res.data.ajuste >= 0 ? '+' : ''}${res.data.ajuste?.toFixed(2)}€)`);
      setShowCashbackModal(null);
      setCashbackAjuste(0);
      setCashbackAbsoluto(0);
      setSetAbsolutoMode(false);
      setMotivoCashback('');
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ajustar cashback');
    }
  };

  const handleResetCashback = async (cliente) => {
    if (!window.confirm(`Zerar DEFINITIVAMENTE o cashback de ${cliente.nome_cliente}?\n\nIsto expira todas as transacções acumuladoras. O saldo não regressará.`)) return;
    try {
      const clienteId = cliente.id_cartao_rfid || cliente.id;
      const res = await api.post(`/parcerias/clientes-rfid/${clienteId}/reset-cashback`);
      toast.success(`Cashback zerado (${res.data.transacoes_expiradas} txn(s) expirada(s))`);
      setShowCashbackModal(null);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao zerar cashback');
    }
  };

  const handleLimparHistoricoPreRegisto = async (cliente) => {
    if (!cliente.id) {
      toast.error('Cliente sem id MongoDB — não é possível executar');
      return;
    }
    try {
      // 1. Dry-run para mostrar o impacto
      const dry = await api.post(
        `/parcerias/clientes-rfid/${cliente.id}/limpar-historico-pre-registo?dry_run=true`
      );
      const d = dry.data;
      const msg = `Vai apagar transações anteriores à criação deste cliente:\n\n` +
        `• ${d.transacoes_pontos_a_remover} transações de pontos (-${d.delta_pontos} pts)\n` +
        `• ${d.transacoes_cashback_a_remover} transações de cashback (-${d.delta_cashback}€)\n\n` +
        `Cliente: ${cliente.nome_cliente}\nData registo: ${d.data_registo?.substring(0, 10)}\n\n` +
        `Esta acção é IRREVERSÍVEL. Continuar?`;
      if (!window.confirm(msg)) return;
      // 2. Executar
      const res = await api.post(
        `/parcerias/clientes-rfid/${cliente.id}/limpar-historico-pre-registo?dry_run=false`
      );
      toast.success(
        `Limpeza concluída. Pontos: ${res.data.saldo_pontos_final} · Cashback: ${res.data.saldo_cashback_final}€`
      );
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar histórico');
    }
  };

  const handleAtribuirBonusEmFalta = async (cliente) => {
    if (!cliente.id) {
      toast.error('Cliente sem id MongoDB — não é possível executar');
      return;
    }
    try {
      const res = await api.post(
        `/parcerias/clientes-rfid/${cliente.id}/atribuir-bonus-em-falta`
      );
      if (res.data.ja_tinha) {
        toast.info('Cliente já tem bónus atribuído.');
      } else {
        toast.success(`Bónus de ${res.data.valor}€ atribuído (válido até ${res.data.expira_em?.substring(0, 10)})`);
      }
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir bónus');
    }
  };


  // Normalização robusta: lowercase + remoção de acentos + trim
  // (resolve bug onde "jose" não encontrava "José Silva" em produção)
  const normalizar = (s) => {
    if (s === null || s === undefined) return '';
    return String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const clientesFiltrados = clientes.filter(cliente => {
    if (!mostrarArquivados && cliente.estado_cartao === 'Desfidelizado') return false;
    if (!pesquisa) return true;
    const termo = normalizar(pesquisa);
    if (!termo) return true;
    return (
      normalizar(cliente.id_cartao_rfid).includes(termo) ||
      normalizar(cliente.nome_cliente).includes(termo) ||
      normalizar(cliente.telefone).includes(termo) ||
      normalizar(cliente.email).includes(termo) ||
      normalizar(cliente.nif).includes(termo) ||
      normalizar(cliente.id).includes(termo)
    );
  });


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gestao-clientes-rfid">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Clientes Janeira</h2>
            <ModuleInfo
              descricao="Gestao de clientes do programa de fidelizacao Janeira. Registo via cartao RFID (loja) ou App movel. Cada cliente acumula pontos por compras, resgata vales e pode participar em missoes e campanhas."
              dados="MongoDB colecao 'clientes' (fonte unica de verdade). O campo 'associacoes' define a ELEGIBILIDADE do cliente: quais parcerias comerciais e perfis de beneficio se aplicam. Cada alteracao de associacao fica registada no log de auditoria (quem, quando, que parceria)."
              calculo="Pontos = acumulados por compras (pontos_por_euro x valor). Total acumulado = soma de todas as compras. Saldo = creditos - debitos. NOTA: 'Perfil beneficio' (ex: Staff, Senior) = desconto directo calculado no momento da compra. 'Parceria empresa' (ex: Farmacia X) = gera vale de desconto."
              legendas="Ativo (verde) = cartao funcional. Inativo = suspenso. Desfidelizado = arquivado. App (azul) = registado via aplicacao movel. Associacoes = lista de parcerias/perfis que determinam os beneficios do cliente."
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Gestão de clientes do programa Janeira
          </p>
        </div>
        {canManage && (
        <Button onClick={() => handleOpenModal()} data-testid="btn-novo-cliente">
          <Plus className="h-4 w-4 mr-2" />
          Novo Cliente
        </Button>
        )}
      </div>

      {/* KPIs Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="clientes-kpi-resumo">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Total Clientes</p>
            <p className="text-xl font-bold text-emerald-600" data-testid="kpi-total-clientes">{clientes.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Activos</p>
            <p className="text-xl font-bold text-blue-600" data-testid="kpi-clientes-activos">
              {clientes.filter(c => c.estado_cliente === 'ativo' || !c.estado_cliente).length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="p-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Via App</p>
            <p className="text-xl font-bold text-violet-600" data-testid="kpi-clientes-app">
              {clientes.filter(c => c.tipo_registo === 'app').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Pesquisa</p>
            <p className="text-xl font-bold text-amber-600" data-testid="kpi-clientes-filtrados">
              {clientesFiltrados.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pesquisa + Toggle arquivados */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por ID, nome, telefone, NIF..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="pl-10"
            data-testid="input-pesquisa-clientes"
            autoFocus
          />
        </div>
        {clientes.length > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="contagem-clientes-carregados">
            {clientes.length} clientes
          </span>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="toggle-arquivados-label">
          <input
            type="checkbox"
            data-testid="toggle-arquivados"
            checked={mostrarArquivados}
            onChange={(e) => {
              const v = e.target.checked;
              setMostrarArquivados(v);
              loadClientes(v);
            }}
            className="h-4 w-4 cursor-pointer"
          />
          Mostrar arquivados/desfidelizados
        </label>
        <Button
          variant="outline"
          size="sm"
          data-testid="diagnostico-xd-btn"
          onClick={async () => {
            try {
              const res = await api.get('/xd-clientes/diagnostico');
              const t = res.data.totais;
              const msg = [
                `📊 DIAGNÓSTICO XD ↔ APP`,
                ``,
                `• Clientes XD (staging): ${t.clientes_xd_staging}`,
                `• Clientes na app: ${t.clientes_app_total}`,
                `   - com xd_id ou origem XD: ${t.clientes_app_com_xd}`,
                `   - arquivados: ${t.clientes_app_arquivados}`,
                ``,
                `⚠️ Em falta na app: ${t.em_falta_com_nif} (com NIF real)`,
                `   - XD sem NIF: ${t.xd_sem_nif}`,
                `   - "Consumidor Final": ${t.xd_consumidor_final}`,
                ``,
                res.data.interpretacao,
              ].join('\n');
              if (t.em_falta_com_nif > 0) {
                if (window.confirm(msg + '\n\nQueres IMPORTAR todos automaticamente agora?')) {
                  const r2 = await api.post('/xd-clientes/auto-import-todos');
                  toast.success(`✅ ${r2.data.criados_count} importados · ${r2.data.saltados_count} saltados`);
                  loadClientes(mostrarArquivados);
                }
              } else {
                alert(msg);
              }
            } catch (e) {
              toast.error('Falha: ' + (e.response?.data?.detail || e.message));
            }
          }}
        >
          Diagnóstico XD
        </Button>
      </div>

      {/* Lista */}
      <Card>
        <CardContent className="pt-6 overflow-x-auto">
          {clientesFiltrados.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="lista-vazia">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum cliente encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">ID Cartão</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Pontos</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Cashback</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Total Acum.</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Registo</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientesFiltrados.map(cliente => (
                  <TableRow 
                    key={cliente.id} 
                    className={`cursor-pointer hover:bg-muted/50 ${cliente.estado_cliente === 'arquivado' ? 'opacity-60 bg-amber-50/50' : ''}`}
                    onClick={() => loadHistorico(cliente)}
                    data-testid={`row-cliente-${cliente.id_cartao_rfid || cliente.id}`}
                  >
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {cliente.id_cartao_rfid || (
                        (['app', 'legacy_app'].includes(cliente.origem_cliente) || cliente.app_id)
                          ? <Badge variant="outline" className="text-[10px] text-blue-600" data-testid={`badge-app-${cliente.id}`}>App</Badge>
                          : (['xd', 'xd_auto_compra'].includes(cliente.origem_cliente) || cliente.xd_id)
                            ? <Badge variant="outline" className="text-[10px] text-gray-700 border-gray-400 bg-gray-50" data-testid={`badge-xd-${cliente.id}`}>XD</Badge>
                            : <Badge variant="outline" className="text-[10px] text-red-700 border-red-400 bg-red-50" data-testid={`badge-sem-cartao-${cliente.id}`}>Sem cartão</Badge>
                      )}
                      {cliente.estado_cliente === 'arquivado' && (
                        <Badge variant="outline" className="text-[9px] ml-1 text-amber-700 border-amber-400 bg-amber-50">Arquivado</Badge>
                      )}
                      {cliente.estado_cartao === 'Desfidelizado' && (
                        <Badge variant="outline" className="text-[9px] ml-1 text-gray-600 border-gray-400 bg-gray-50">Desfidelizado</Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[140px]">
                      <div className="truncate font-medium text-sm">{cliente.nome_cliente || cliente.nome || '-'}</div>
                      {(cliente.associacoes_info || []).length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-0.5">
                          {(cliente.associacoes_info || []).slice(0, 2).map(a => (
                            <Badge key={a.id} variant={a.tipo === 'perfil_beneficio' ? 'default' : 'outline'} className="text-[9px] px-1 py-0">
                              {a.nome.length > 12 ? a.nome.slice(0, 12) + '...' : a.nome}
                            </Badge>
                          ))}
                          {(cliente.associacoes_info || []).length > 2 && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0">+{(cliente.associacoes_info || []).length - 2}</Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {cliente.telefone || cliente.email || '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{cliente.saldo_pontos || 0}</TableCell>
                    <TableCell className="text-right font-medium text-purple-600 tabular-nums whitespace-nowrap">{cliente.saldo_cashback?.toFixed(2) || '0.00'}€</TableCell>
                    <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                      €{cliente.total_acumulado_janeira?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={cliente.estado_cartao === 'Ativo' ? 'success' : 'secondary'} className="text-[10px] px-1.5">
                        {cliente.estado_cartao === 'Ativo' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      <div>{new Date(cliente.data_registo).toLocaleDateString('pt-PT')}</div>
                      {cliente.tipo_registo === 'app' && (
                        <Badge variant="outline" className="text-[9px] mt-0.5 text-purple-600 border-purple-200">App</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => loadHistorico(cliente)} title="Histórico" data-testid={`btn-historico-${cliente.id_cartao_rfid || cliente.id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenModal(cliente)} title="Editar">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        {isAdmin && (
                          <div className="relative">
                            <Button
                              ref={(el) => { if (el) avancadoRefs.current[cliente.id_cartao_rfid || cliente.id] = el; }}
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-amber-600"
                              onClick={() => {
                                const key = cliente.id_cartao_rfid || cliente.id;
                                if (avancadoOpenFor === key) {
                                  setAvancadoOpenFor(null);
                                  return;
                                }
                                // Detectar se abre acima ou abaixo conforme posição no viewport
                                const btn = avancadoRefs.current[key];
                                if (btn) {
                                  const rect = btn.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const menuHeight = 165; // 4 items + headers
                                  setAvancadoOpenFor(key);
                                  setAvancadoOpenDir(spaceBelow < menuHeight ? 'up' : 'down');
                                } else {
                                  setAvancadoOpenFor(key);
                                  setAvancadoOpenDir('down');
                                }
                              }}
                              title="Avançado (ajustes manuais — risco operacional)"
                              data-testid={`btn-avancado-${cliente.id_cartao_rfid || cliente.id}`}
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                            {avancadoOpenFor === (cliente.id_cartao_rfid || cliente.id) && (
                              <>
                                <div className="fixed inset-0 z-30" onClick={() => setAvancadoOpenFor(null)} />
                                <div
                                  className={`absolute right-0 z-40 bg-white border border-amber-200 rounded-md shadow-lg py-1 min-w-[180px] ${
                                    avancadoOpenDir === 'up' ? 'bottom-8' : 'top-8'
                                  }`}
                                  data-testid={`menu-avancado-${cliente.id_cartao_rfid || cliente.id}`}
                                >
                                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-amber-700 border-b bg-amber-50">
                                    Avançado · admin
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => { setShowEditarPontosModal(cliente); setPontosAjuste(0); setMotivoAjuste(''); setAvancadoOpenFor(null); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-2 text-blue-700"
                                    data-testid={`btn-ajustar-pontos-${cliente.id_cartao_rfid || cliente.id}`}
                                  >
                                    <Star className="h-3.5 w-3.5" />
                                    Ajustar pontos
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setShowCashbackModal(cliente); setCashbackAjuste(0); setCashbackAbsoluto(cliente.saldo_cashback || 0); setSetAbsolutoMode(false); setMotivoCashback(''); setAvancadoOpenFor(null); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 flex items-center gap-2 text-purple-700"
                                    data-testid={`btn-ajustar-cashback-${cliente.id_cartao_rfid || cliente.id}`}
                                  >
                                    <Wallet className="h-3.5 w-3.5" />
                                    Ajustar cashback
                                  </button>
                                  <div className="border-t my-1" />
                                  <button
                                    type="button"
                                    onClick={() => { setShowResetModal(cliente); setAvancadoOpenFor(null); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 flex items-center gap-2 text-red-700"
                                    data-testid={`btn-reset-pontos-${cliente.id_cartao_rfid || cliente.id}`}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reset pontos
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { handleResetCashback(cliente); setAvancadoOpenFor(null); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 flex items-center gap-2 text-red-700"
                                    data-testid={`btn-reset-cashback-${cliente.id_cartao_rfid || cliente.id}`}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reset cashback
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {canManage && cliente.estado_cliente === 'arquivado' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleReactivar(cliente)} title="Reactivar" data-testid={`btn-reactivar-${cliente.id_cartao_rfid || cliente.id}`}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canManage && cliente.estado_cliente !== 'arquivado' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => setShowDeleteConfirm(cliente)} title="Arquivar" data-testid={`btn-delete-cliente-${cliente.id_cartao_rfid}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Cliente' : 'Novo Cliente'}
            </DialogTitle>
            {editando?.tipo_registo === 'app' && (
              <p className="text-xs text-blue-600 mt-1">Registado via App Movel</p>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* ID interno - mostrar apenas ao editar */}
            {editando && (
              <div className="p-3 bg-muted/50 rounded-lg border">
                <Label className="text-xs text-muted-foreground">ID Interno do Cliente</Label>
                <p className="font-mono text-sm mt-1 select-all" data-testid="cliente-id-interno">{editando.id}</p>
              </div>
            )}

            <div>
              <Label>ID do Cartão (RFID ou interno) {!editando && '*'}</Label>
              <Input
                value={formData.id_cartao_rfid}
                onChange={(e) => setFormData(prev => ({ ...prev, id_cartao_rfid: e.target.value }))}
                placeholder={editando && !editando.id_cartao_rfid ? 'Associar cartão RFID a este cliente...' : 'Aproxime o cartão ou introduza o ID...'}
                disabled={!!editando && !!editando.id_cartao_rfid}
                data-testid="input-id-cartao-rfid"
              />
              {editando && !editando.id_cartao_rfid && (
                <p className="text-xs text-amber-600 mt-1">Este cliente não tem cartão associado. Introduza um ID para associar.</p>
              )}
            </div>

            <div>
              <Label>Nome do Cliente (opcional)</Label>
              <Input
                value={formData.nome_cliente}
                onChange={(e) => setFormData(prev => ({ ...prev, nome_cliente: e.target.value }))}
                placeholder="Nome para identificação interna"
              />
            </div>

            {!editando && (
              <div>
                <Label className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Local de Registo
                </Label>
                <select
                  value={formData.local_registo}
                  onChange={(e) => setFormData(prev => ({ ...prev, local_registo: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="select-local-registo"
                >
                  <option value="">Selecionar local...</option>
                  {locaisRegisto.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone (opcional)</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="912345678"
                />
              </div>
              <div>
                <Label>Email (opcional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NIF <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.nif}
                  onChange={(e) => setFormData(prev => ({ ...prev, nif: e.target.value }))}
                  placeholder="123456789"
                  required
                  data-testid="input-nif"
                />
              </div>
              <div>
                <Label>Morada (opcional)</Label>
                <Input
                  value={formData.morada}
                  onChange={(e) => setFormData(prev => ({ ...prev, morada: e.target.value }))}
                  placeholder="Rua..."
                  data-testid="input-morada"
                />
              </div>
            </div>

            {/* Data de Nascimento */}
            <div>
              <Label>Data de Nascimento (opcional)</Label>
              <Input
                type="date"
                value={formData.data_nascimento || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, data_nascimento: e.target.value }))}
                data-testid="input-data-nascimento"
              />
            </div>

            {/* Consentimentos RGPD (Opção B — substituem checkboxes legacy) */}
            <div className="border rounded-lg p-4 bg-amber-50/60 border-amber-200" data-testid="rgpd-consents-block">
              <div className="flex items-start gap-2 mb-2">
                <div className="flex-1">
                  <Label className="text-sm font-semibold block">Consentimentos RGPD</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Registo manual após cliente assinar consentimento em papel na loja. Cada alteração fica guardada em log de auditoria (origem: <span className="font-medium">loja</span>).
                  </p>
                </div>
              </div>
              <div className="space-y-2 mt-2">
                <label className="flex items-start gap-2 cursor-pointer text-sm bg-white/70 rounded p-2 border border-amber-100">
                  <input
                    type="checkbox"
                    checked={!!formData.consentimentos?.privacidade}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      consentimentos: { ...prev.consentimentos, privacidade: e.target.checked }
                    }))}
                    className="rounded mt-0.5"
                    data-testid="check-rgpd-privacidade"
                  />
                  <span>
                    <span className="font-medium text-rose-700">Privacidade (obrigatório)</span>
                    <span className="block text-xs text-muted-foreground">Cliente leu e aceita a Política de Privacidade e tratamento dos seus dados pessoais.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer text-sm bg-white/70 rounded p-2 border border-amber-100">
                  <input
                    type="checkbox"
                    checked={!!formData.consentimentos?.promocional}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      consentimentos: { ...prev.consentimentos, promocional: e.target.checked }
                    }))}
                    className="rounded mt-0.5"
                    data-testid="check-rgpd-promocional"
                  />
                  <span>
                    <span className="font-medium">Comunicações Promocionais (opcional, RGPD/CNPD)</span>
                    <span className="block text-xs text-muted-foreground">Umbrella legacy. Por si só JÁ NÃO autoriza envios — é preciso autorizar cada canal individualmente abaixo.</span>
                  </span>
                </label>

                {/* 🆕 P2-B — Consentimentos por canal (grid 2x2 sempre visível) */}
                <div className="bg-white/90 rounded p-3 border border-amber-200" data-testid="canais-promocional-grid">
                  <div className="text-xs font-semibold text-amber-900 mb-2 uppercase tracking-wide">
                    Canais Promocionais (consentimento por canal — CNPD)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { canal: 'sms', label: 'SMS' },
                      { canal: 'whatsapp', label: 'WhatsApp' },
                      { canal: 'email', label: 'Email' },
                      { canal: 'push', label: 'Push / App' },
                    ].map(({ canal, label }) => {
                      const v = formData.consentimentos_canais?.[canal];
                      const origStatus = formData.canais_estado_original?.[canal]?.status;
                      // Badge de estado original (read-only)
                      let badge = null;
                      if (origStatus === 'aceite_canal') badge = { txt: 'Explícito', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
                      else if (origStatus === 'aceite_legado') badge = { txt: 'Legado', cls: 'bg-amber-100 text-amber-800 border-amber-300' };
                      else if (origStatus === 'recusado_canal') badge = { txt: 'Recusado (canal)', cls: 'bg-rose-100 text-rose-700 border-rose-300' };
                      else if (origStatus === 'recusado_umbrella') badge = { txt: 'Recusado (umbrella)', cls: 'bg-rose-50 text-rose-600 border-rose-200' };
                      else badge = { txt: 'Não definido', cls: 'bg-gray-100 text-gray-600 border-gray-300' };

                      // Tri-state cycle: null → true → false → null
                      const toggleTri = () => {
                        setFormData(prev => {
                          const cur = prev.consentimentos_canais?.[canal];
                          const next = cur === null || cur === undefined ? true : cur === true ? false : null;
                          return {
                            ...prev,
                            consentimentos_canais: { ...prev.consentimentos_canais, [canal]: next }
                          };
                        });
                      };

                      const dotCls = v === true ? 'bg-emerald-500'
                        : v === false ? 'bg-rose-500'
                        : 'bg-gray-300';

                      return (
                        <button
                          key={canal}
                          type="button"
                          onClick={toggleTri}
                          className={`text-left p-2 rounded border transition-colors ${
                            v === true ? 'bg-emerald-50 border-emerald-300'
                            : v === false ? 'bg-rose-50 border-rose-300'
                            : 'bg-white border-gray-200 hover:bg-amber-50'
                          }`}
                          data-testid={`canal-toggle-${canal}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block h-3 w-3 rounded-full ${dotCls}`}></span>
                              <span className="text-sm font-medium">{label}</span>
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls}`} data-testid={`canal-badge-${canal}`}>
                              {badge.txt}
                            </span>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-1">
                            {v === true ? '✅ Aceite (será gravado como explícito)' :
                             v === false ? '🚫 Recusa explícita' :
                             '— Não alterado (mantém estado actual)'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[11px] text-amber-900">
                    <strong>Legenda:</strong> Explícito = consentimento por canal (NOVO). Legado = compat anterior (continua válido). Recusado = bloqueio explícito. Clica em cada canal para alternar: <span className="font-mono">Não alterado → ✅ Aceite → 🚫 Recusa → Não alterado</span>.
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-sm bg-white/70 rounded p-2 border border-amber-100">
                  <input
                    type="checkbox"
                    checked={!!formData.consentimentos?.personalizacao}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      consentimentos: { ...prev.consentimentos, personalizacao: e.target.checked }
                    }))}
                    className="rounded mt-0.5"
                    data-testid="check-rgpd-personalizacao"
                  />
                  <span>
                    <span className="font-medium">Personalização (opcional)</span>
                    <span className="block text-xs text-muted-foreground">Permitir uso do histórico de compras para sugestões e ofertas personalizadas.</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Associações a Parcerias / Perfis - separadas em duas colunas */}
            {parceriasDisponiveis.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {/* Coluna Parcerias (Empresa) - Sempre ativas automaticamente */}
                <div>
                  <Label className="text-sm font-medium">Parcerias</Label>
                  <p className="text-xs text-green-600 mb-1">Associadas automaticamente a todos os clientes</p>
                  <div className="mt-1 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-green-50/50">
                    {parceriasDisponiveis.filter(p => p.tipo_parceria !== 'perfil_beneficio').length > 0 
                      ? parceriasDisponiveis.filter(p => p.tipo_parceria !== 'perfil_beneficio').map(p => (
                        <label key={p.id} className="flex items-center gap-2 text-sm opacity-80">
                          <input
                            type="checkbox"
                            checked={true}
                            disabled={true}
                            className="rounded"
                            data-testid={`assoc-empresa-${p.id}`}
                          />
                          <span>{p.nome_entidade}</span>
                        </label>
                      ))
                      : <span className="text-xs text-muted-foreground">Nenhuma parceria disponível</span>
                    }
                  </div>
                </div>
                {/* Coluna Perfis de Benefício - Seleção manual */}
                <div>
                  <Label className="text-sm font-medium">Perfis de Benefício</Label>
                  <p className="text-xs text-amber-600 mb-1">Selecionar manualmente</p>
                  <div className="mt-1 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-amber-50/50">
                    {parceriasDisponiveis.filter(p => p.tipo_parceria === 'perfil_beneficio').length > 0 
                      ? parceriasDisponiveis.filter(p => p.tipo_parceria === 'perfil_beneficio').map(p => (
                        <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={formData.associacoes.includes(p.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({ ...prev, associacoes: [...prev.associacoes, p.id] }));
                              } else {
                                setFormData(prev => ({ ...prev, associacoes: prev.associacoes.filter(a => a !== p.id) }));
                              }
                            }}
                            className="rounded"
                            data-testid={`assoc-perfil-${p.id}`}
                          />
                          <span>{p.nome_entidade}</span>
                        </label>
                      ))
                      : <span className="text-xs text-muted-foreground">Nenhum perfil disponível</span>
                    }
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {editando ? 'Guardar' : 'Registar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Substituir Cartão */}
      <Dialog open={!!showSubstituirModal} onOpenChange={() => setShowSubstituirModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Substituir Cartão</DialogTitle>
            <DialogDescription>
              O cartão antigo será desativado e o histórico de vales será transferido para o novo cartão.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Cartão Atual</Label>
              <Input value={showSubstituirModal?.id_cartao_rfid} disabled />
            </div>
            <div>
              <Label>Novo ID do Cartão *</Label>
              <Input
                value={novoIdCartao}
                onChange={(e) => setNovoIdCartao(e.target.value)}
                placeholder="Aproxime o novo cartão..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubstituirModal(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSubstituir}>
              Substituir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Arquivamento */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arquivar Cliente</DialogTitle>
            <DialogDescription>
              <span className="block mb-2">
                Vais arquivar o cliente {showDeleteConfirm?.nome ? <strong>"{showDeleteConfirm.nome}"</strong> : ''}{showDeleteConfirm?.id_cartao_rfid ? ` (cartão ${showDeleteConfirm.id_cartao_rfid})` : ''}.
              </span>
              <span className="block text-sm bg-blue-50 border border-blue-200 rounded p-2">
                ✓ Saldos, pontos, cashback e histórico são <strong>preservados</strong>.<br/>
                ✓ Cliente fica oculto da lista normal.<br/>
                ✓ Pode ser reactivado a qualquer momento via "Mostrar arquivados".<br/>
                ✗ Motor não dará mais pontos/cashback enquanto estiver arquivado.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="btn-confirm-arquivar">
              Arquivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Histórico Completo */}
      <Dialog open={!!showHistoricoModal} onOpenChange={() => { setShowHistoricoModal(null); setHistorico(null); }}>
        <DialogContent className="!max-w-6xl !grid-rows-[auto_1fr] !gap-0 !h-[90vh] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Cliente: {showHistoricoModal?.nome_cliente || 'Cliente'}
            </DialogTitle>
            <DialogDescription>
              ID: {showHistoricoModal?.id_cartao_rfid || showHistoricoModal?.id} | 
              Pontos: {showHistoricoModal?.saldo_pontos || 0} | 
              Total: €{showHistoricoModal?.total_acumulado_janeira?.toFixed(2) || '0.00'}
              {showHistoricoModal?.criado_por_nome && (
                <span className="ml-2">| Registado por: <strong>{showHistoricoModal.criado_por_nome}</strong></span>
              )}
              {showHistoricoModal?.local_registo && (
                <span className="ml-2">| Local: <strong>{showHistoricoModal.local_registo}</strong></span>
              )}
              {showHistoricoModal?.tipo_registo && (
                <span className="ml-2">| Tipo: <strong>{showHistoricoModal.tipo_registo === 'app' ? 'App' : 'Loja'}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto min-h-0 pr-1 flex-1">
            {historicoLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : historico ? (
              <Tabs defaultValue="operacao" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="operacao" className="flex items-center gap-1" data-testid="tab-cliente-operacao">
                    <Wallet className="h-3 w-3" /> Operação
                  </TabsTrigger>
                  <TabsTrigger value="movimentos" className="flex items-center gap-1">
                    <ShoppingBag className="h-3 w-3" /> Movimentos
                  </TabsTrigger>
                  <TabsTrigger value="vales" className="flex items-center gap-1">
                    <Gift className="h-3 w-3" /> Vales
                  </TabsTrigger>
                  <TabsTrigger value="missoes" className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Missões
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="operacao" className="mt-4 pb-8">
                  <ConsultaOperacional
                    user={user}
                    initialClientId={showHistoricoModal?.id_cartao_rfid || showHistoricoModal?.id}
                    embedded={true}
                  />
                </TabsContent>
                
                <TabsContent value="movimentos" className="mt-4">
                  {historico.movimentos?.length > 0 ? (() => {
                    // Sort movimentos
                    const sorted = [...historico.movimentos].sort((a, b) => {
                      const dir = movSort.dir === 'asc' ? 1 : -1;
                      const f = movSort.field;
                      let va = a[f], vb = b[f];
                      if (f === 'data') {
                        va = va ? new Date(va).getTime() : 0;
                        vb = vb ? new Date(vb).getTime() : 0;
                      } else if (f === 'valor' || f === 'pontos' || f === 'cashback') {
                        va = Number(va) || 0;
                        vb = Number(vb) || 0;
                      } else {
                        va = String(va || '').toLowerCase();
                        vb = String(vb || '').toLowerCase();
                      }
                      if (va < vb) return -1 * dir;
                      if (va > vb) return 1 * dir;
                      return 0;
                    });
                    const totalPages = Math.ceil(sorted.length / MOV_PER_PAGE);
                    const paginated = sorted.slice(movPage * MOV_PER_PAGE, (movPage + 1) * MOV_PER_PAGE);
                    const SortIcon = ({ field }) => {
                      if (movSort.field !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
                      return movSort.dir === 'asc' 
                        ? <ArrowUp className="h-3 w-3 ml-1 text-emerald-600" /> 
                        : <ArrowDown className="h-3 w-3 ml-1 text-emerald-600" />;
                    };
                    const toggleSort = (field) => {
                      setMovSort(prev => prev.field === field 
                        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                        : { field, dir: 'desc' });
                      setMovPage(0);
                    };
                    return (
                      <>
                        <div className="flex items-center justify-between mb-2 px-1">
                          <span className="text-xs text-muted-foreground">{sorted.length} movimentos</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={movPage === 0} onClick={() => setMovPage(p => p - 1)} data-testid="mov-prev-page">
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-xs font-medium">{movPage + 1} / {totalPages}</span>
                            <Button variant="outline" size="sm" disabled={movPage >= totalPages - 1} onClick={() => setMovPage(p => p + 1)} data-testid="mov-next-page">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('data')} data-testid="sort-data">
                                <span className="flex items-center">Data<SortIcon field="data" /></span>
                              </TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('numero_fatura')} data-testid="sort-fatura">
                                <span className="flex items-center">Fatura<SortIcon field="numero_fatura" /></span>
                              </TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('registado_por')} data-testid="sort-operador">
                                <span className="flex items-center">Operador<SortIcon field="registado_por" /></span>
                              </TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('local_compra')} data-testid="sort-local">
                                <span className="flex items-center">Local<SortIcon field="local_compra" /></span>
                              </TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50 text-right" onClick={() => toggleSort('valor')} data-testid="sort-valor">
                                <span className="flex items-center justify-end">Valor<SortIcon field="valor" /></span>
                              </TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50 text-right" onClick={() => toggleSort('pontos')} data-testid="sort-pontos">
                                <span className="flex items-center justify-end">Pontos<SortIcon field="pontos" /></span>
                              </TableHead>
                              <TableHead className="text-right text-xs text-muted-foreground">Saldo Pts</TableHead>
                              <TableHead className="cursor-pointer select-none hover:bg-muted/50 text-right" onClick={() => toggleSort('cashback')} data-testid="sort-cashback">
                                <span className="flex items-center justify-end">Cashback<SortIcon field="cashback" /></span>
                              </TableHead>
                              <TableHead className="text-right text-xs text-muted-foreground">Saldo CB</TableHead>
                              <TableHead title="Reconciliacao com fatura XD">Rec.</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paginated.map((m, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm whitespace-nowrap">{m.data ? new Date(m.data).toLocaleDateString('pt-PT') : '-'}</TableCell>
                                <TableCell className="text-xs">{m.numero_fatura || '-'}</TableCell>
                                <TableCell className="text-sm">{m.registado_por || '-'}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{m.local_compra || '-'}</TableCell>
                                <TableCell className="text-right font-medium">{'\u20AC'}{(m.valor || 0).toFixed(2)}</TableCell>
                                <TableCell className={`text-right font-medium ${m.pontos > 0 ? 'text-green-600' : m.pontos < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                  {m.pontos > 0 ? `+${m.pontos}` : m.pontos || '-'}
                                </TableCell>
                                <TableCell className="text-right text-xs text-muted-foreground font-mono">
                                  {typeof m.saldo_pontos_apos === 'number' ? m.saldo_pontos_apos : '-'}
                                </TableCell>
                                <TableCell className={`text-right ${m.cashback > 0 ? 'text-blue-600 font-medium' : m.cashback < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                  {m.cashback > 0 ? `+\u20AC${m.cashback.toFixed(2)}` : m.cashback < 0 ? `-\u20AC${Math.abs(m.cashback).toFixed(2)}` : '-'}
                                </TableCell>
                                <TableCell className="text-right text-xs text-muted-foreground font-mono">
                                  {typeof m.saldo_cashback_apos === 'number' ? `\u20AC${m.saldo_cashback_apos.toFixed(2)}` : '-'}
                                </TableCell>
                                <TableCell>
                                  {m.numero_fatura?.startsWith('RES-') ? <Badge variant="secondary" className="text-[10px] px-1" title="Resgate manual (sem fatura XD)">N/A</Badge> : (
                                    <>
                                      {m.reconciliacao === 'reconciliado' && <Badge variant="success" className="text-[10px] px-1" title="Fatura confirmada no XD">OK</Badge>}
                                      {m.reconciliacao === 'falha' && <Badge variant="destructive" className="text-[10px] px-1" title="Divergencia com XD">Falha</Badge>}
                                      {m.reconciliacao === 'pendente' && <Badge variant="outline" className="text-[10px] px-1" title="Aguarda confirmacao do XD">Pend.</Badge>}
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </div>
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t">
                            <Button variant="outline" size="sm" disabled={movPage === 0} onClick={() => setMovPage(0)}>
                              Primeira
                            </Button>
                            <Button variant="outline" size="sm" disabled={movPage === 0} onClick={() => setMovPage(p => p - 1)}>
                              <ChevronLeft className="h-4 w-4" /> Anterior
                            </Button>
                            <span className="text-sm font-medium px-3">Pag. {movPage + 1} de {totalPages}</span>
                            <Button variant="outline" size="sm" disabled={movPage >= totalPages - 1} onClick={() => setMovPage(p => p + 1)}>
                              Seguinte <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" disabled={movPage >= totalPages - 1} onClick={() => setMovPage(totalPages - 1)}>
                              Ultima
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShoppingBag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum movimento registado</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="vales" className="mt-4">
                  {historico.vales?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Parceria</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historico.vales.map((v, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{new Date(v.data_criacao).toLocaleDateString('pt-PT')}</TableCell>
                            <TableCell>{v.parceria_nome}</TableCell>
                            <TableCell>{v.descricao}</TableCell>
                            <TableCell>
                              <Badge variant={v.estado === 'Utilizado' ? 'secondary' : v.estado === 'Ativo' ? 'success' : 'destructive'}>
                                {v.estado}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">€{v.valor?.toFixed(2) || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum vale registado</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="missoes" className="mt-4">
                  {historico.missoes?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Missão</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Prémio</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historico.missoes.map((m, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{m.data_conclusao ? new Date(m.data_conclusao).toLocaleDateString('pt-PT') : '-'}</TableCell>
                            <TableCell>{m.nome}</TableCell>
                            <TableCell>
                              <Badge variant={m.concluida ? 'success' : 'outline'}>
                                {m.concluida ? 'Concluída' : 'Em progresso'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{m.premio_pontos} pts</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma missão registada</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Erro ao carregar histórico</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowHistoricoModal(null); setHistorico(null); }}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Reset de Pontos (Admin) */}
      <Dialog open={!!showResetModal} onOpenChange={() => setShowResetModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Zerar Pontos
            </DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja zerar os pontos de <strong>{showResetModal?.nome_cliente || 'este cliente'}</strong>?
              <br /><br />
              Pontos atuais: <strong>{showResetModal?.saldo_pontos || 0}</strong>
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleResetPontos}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Zerar Pontos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Editar Pontos (Admin) */}
      <Dialog open={!!showEditarPontosModal} onOpenChange={() => setShowEditarPontosModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Star className="h-5 w-5" />
              Ajustar Pontos
            </DialogTitle>
            <DialogDescription>
              Ajustar pontos de <strong>{showEditarPontosModal?.nome_cliente || 'este cliente'}</strong>
              <br />
              Saldo atual: <strong>{showEditarPontosModal?.saldo_pontos || 0} pontos</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Ajuste de pontos</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPontosAjuste(p => p - 10)}
                  className="px-3"
                >-10</Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPontosAjuste(p => p - 1)}
                  className="px-3"
                >-1</Button>
                <Input 
                  type="number" 
                  value={pontosAjuste} 
                  onChange={(e) => setPontosAjuste(parseInt(e.target.value) || 0)}
                  className="w-24 text-center font-bold"
                  data-testid="input-ajuste-pontos"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPontosAjuste(p => p + 1)}
                  className="px-3"
                >+1</Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPontosAjuste(p => p + 10)}
                  className="px-3"
                >+10</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Novo saldo: <strong className={pontosAjuste >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {(showEditarPontosModal?.saldo_pontos || 0) + pontosAjuste} pontos
                </strong>
                {pontosAjuste !== 0 && (
                  <span className={pontosAjuste > 0 ? 'text-green-600 ml-2' : 'text-red-600 ml-2'}>
                    ({pontosAjuste > 0 ? '+' : ''}{pontosAjuste})
                  </span>
                )}
              </p>
            </div>
            
            <div>
              <Label>Motivo do ajuste</Label>
              <Input 
                value={motivoAjuste}
                onChange={(e) => setMotivoAjuste(e.target.value)}
                placeholder="Ex: Correção de erro, Bónus especial..."
                className="mt-1"
                data-testid="input-motivo-ajuste"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarPontosModal(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAjustarPontos}
              disabled={pontosAjuste === 0}
              className={pontosAjuste > 0 ? 'bg-green-600 hover:bg-green-700' : pontosAjuste < 0 ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="btn-confirmar-ajuste"
            >
              <Star className="h-4 w-4 mr-2" />
              {pontosAjuste > 0 ? `Adicionar ${pontosAjuste} pontos` : pontosAjuste < 0 ? `Remover ${Math.abs(pontosAjuste)} pontos` : 'Ajustar Pontos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ajustar Cashback */}
      <Dialog open={!!showCashbackModal} onOpenChange={() => { setShowCashbackModal(null); setSetAbsolutoMode(false); setCashbackAbsoluto(0); }}>
        <DialogContent data-testid="modal-cashback">
          <DialogHeader>
            <DialogTitle>Ajustar Cashback</DialogTitle>
            <DialogDescription>
              Ajustar saldo cashback de <strong>{showCashbackModal?.nome_cliente}</strong>
              {' '}(Saldo actual: <span className="font-mono">{showCashbackModal?.saldo_cashback?.toFixed(2) || '0.00'}€</span>)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Tabs Delta vs Absoluto */}
            <div className="flex border rounded-lg overflow-hidden" data-testid="tabs-modo-cashback">
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium transition-colors ${!setAbsolutoMode ? 'bg-blue-50 text-blue-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                onClick={() => setSetAbsolutoMode(false)}
                data-testid="tab-modo-delta"
              >
                Adicionar / Remover
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium transition-colors border-l ${setAbsolutoMode ? 'bg-amber-50 text-amber-800' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                onClick={() => setSetAbsolutoMode(true)}
                data-testid="tab-modo-absoluto"
              >
                Definir saldo
              </button>
            </div>

            {!setAbsolutoMode ? (
              <>
                <p className="text-xs text-muted-foreground text-center">
                  Adiciona ou remove ao saldo actual.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCashbackAjuste(v => v - 5)} className="px-3">-5€</Button>
                  <Button variant="outline" size="sm" onClick={() => setCashbackAjuste(v => v - 1)} className="px-3">-1€</Button>
                  <Input
                    type="number" step="0.5"
                    value={cashbackAjuste}
                    onChange={(e) => setCashbackAjuste(parseFloat(e.target.value) || 0)}
                    className="w-24 text-center font-bold"
                    data-testid="input-ajuste-cashback"
                  />
                  <Button variant="outline" size="sm" onClick={() => setCashbackAjuste(v => v + 1)} className="px-3">+1€</Button>
                  <Button variant="outline" size="sm" onClick={() => setCashbackAjuste(v => v + 5)} className="px-3">+5€</Button>
                </div>
                {cashbackAjuste !== 0 && (
                  <div className="text-xs text-center text-slate-600">
                    Novo saldo previsto: <span className="font-mono font-semibold">
                      {Math.max(0, (showCashbackModal?.saldo_cashback || 0) + cashbackAjuste).toFixed(2)}€
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground text-center">
                  Define directamente o saldo final. O sistema calcula o ajuste automaticamente.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Label className="text-sm">Saldo final:</Label>
                  <Input
                    type="number" step="0.5" min="0"
                    value={cashbackAbsoluto}
                    onChange={(e) => setCashbackAbsoluto(parseFloat(e.target.value) || 0)}
                    className="w-28 text-center font-bold"
                    data-testid="input-cashback-absoluto"
                  />
                  <span className="text-sm font-mono">€</span>
                </div>
                {cashbackAbsoluto !== (showCashbackModal?.saldo_cashback || 0) && (
                  <div className="text-xs text-center text-slate-600">
                    Equivale a <span className="font-mono font-semibold">
                      {((cashbackAbsoluto - (showCashbackModal?.saldo_cashback || 0)) >= 0 ? '+' : '')}
                      {(cashbackAbsoluto - (showCashbackModal?.saldo_cashback || 0)).toFixed(2)}€
                    </span>
                  </div>
                )}
              </>
            )}

            <div>
              <Label>Motivo</Label>
              <Input
                value={motivoCashback}
                onChange={(e) => setMotivoCashback(e.target.value)}
                placeholder="Ex: Correção, Bónus especial..."
                className="mt-1"
                data-testid="input-motivo-cashback"
              />
            </div>

            <div className="border-t pt-3">
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 w-full" onClick={() => handleResetCashback(showCashbackModal)} data-testid="btn-zerar-cashback">
                <RotateCcw className="h-4 w-4 mr-1" /> Zerar saldo (definitivo)
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-1">
                Expira todas as transacções acumuladoras. O saldo não voltará a aparecer.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCashbackModal(null); setSetAbsolutoMode(false); }}>Cancelar</Button>
            <Button
              onClick={handleAjustarCashback}
              disabled={setAbsolutoMode
                ? cashbackAbsoluto === (showCashbackModal?.saldo_cashback || 0)
                : cashbackAjuste === 0}
              className={
                setAbsolutoMode
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : cashbackAjuste > 0
                    ? 'bg-green-600 hover:bg-green-700'
                    : cashbackAjuste < 0 ? 'bg-red-600 hover:bg-red-700' : ''
              }
              data-testid="btn-confirmar-cashback"
            >
              <Wallet className="h-4 w-4 mr-2" />
              {setAbsolutoMode
                ? `Definir saldo a ${cashbackAbsoluto}€`
                : cashbackAjuste > 0
                  ? `Adicionar ${cashbackAjuste}€`
                  : cashbackAjuste < 0 ? `Remover ${Math.abs(cashbackAjuste)}€` : 'Ajustar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoClientes;
